import { ACCOUNT_STATUSES_BLOCKING_ORDERS, AccountStatus, type Customer } from '@lombok/shared';
import { accountStatusText } from '@/features/accounts/accountActions';

export interface AccountNotice {
  tone: 'danger' | 'warning';
  text: string;
}

/**
 * What the distributor portal tells its users about their own account. A suspended, rejected or closed account
 * can still read everything but cannot upload payments or create orders (the API refuses both); an account in
 * review is only told so. An active or on-hold account gets no notice.
 */
export function distributorNotice(status: AccountStatus, reason?: string): AccountNotice | undefined {
  if (ACCOUNT_STATUSES_BLOCKING_ORDERS.includes(status)) {
    const why = reason?.trim() ? ` Motivo: ${reason.trim().replace(/[.\s]+$/, '')}.` : '';
    return {
      tone: 'danger',
      text: `Tu cuenta de distribuidor está ${accountStatusText(status)}: puedes consultar tus datos, pero no subir pagos ni crear pedidos.${why} Contacta a Lombok para reactivarla.`,
    };
  }
  if (status === AccountStatus.PENDING) {
    return {
      tone: 'warning',
      text: 'Tu cuenta de distribuidor está en revisión. Un administrador la activará cuando termine la revisión.',
    };
  }
  return undefined;
}

/** How many sub-accounts the distributor has and how much they owe in total (USD minor units). */
export function subaccountTotals(customers: readonly Pick<Customer, 'openBalanceMinorUsd'>[]): {
  count: number;
  openBalanceMinorUsd: number;
} {
  return {
    count: customers.length,
    openBalanceMinorUsd: customers.reduce((sum, c) => sum + c.openBalanceMinorUsd, 0),
  };
}

/** The sub-accounts that owe the most, largest first (those with nothing pending are left out). */
export function topBalances<T extends Pick<Customer, 'openBalanceMinorUsd'>>(customers: readonly T[], max = 5): T[] {
  return customers
    .filter((c) => c.openBalanceMinorUsd > 0)
    .sort((a, b) => b.openBalanceMinorUsd - a.openBalanceMinorUsd)
    .slice(0, max);
}
